<!-- Header Toolbar (shared component — nav + logout preserved) -->
<app-header-toolbar
  [userDisplayName]="getUserDisplayName()"
  (logout)="logout()">
</app-header-toolbar>

<div class="page">
  <div class="wrap">

    <!-- ============================ Hero ============================ -->
    <header class="quotes-hero">
      <div class="quotes-hero-copy">
        <p class="eyebrow">Producer console</p>
        <h1 class="hero-title">{{ pageTitle }}</h1>
        <p class="hero-sub">{{ pageSubtitle }}</p>
      </div>

      <!-- Prominent gold CTA (the one primary action on this view) -->
      <button
        type="button"
        class="cta"
        (click)="createNewQuote()">
        <mat-icon aria-hidden="true">add</mat-icon>
        New Quote
      </button>
    </header>

    <!-- ========================= Quotes card ========================= -->
    <section class="card">
      <div class="card-head">
        <h2>{{ pageTitle }} management</h2>
        <span class="card-badge mono">{{ totalQuotes }} total</span>
      </div>

      <div class="card-body">
        <!-- Status pill-box — quotes & accounts are one book, filtered by status
             (Teddy 2026-07-02). "All" plus each status. -->
        <div class="status-pills">
          <mat-chip-listbox
            class="status-pill-box"
            [ngModel]="selectedStatus"
            (ngModelChange)="onStatusPillChange($event)"
            aria-label="Filter by status">
            <mat-chip-option value="" [selected]="selectedStatus === ''">All</mat-chip-option>
            <mat-chip-option
              *ngFor="let status of statusOptions"
              [value]="status.value"
              [selected]="selectedStatus === status.value">
              {{ status.displayName }}
            </mat-chip-option>
          </mat-chip-listbox>
        </div>

        <!-- Filters -->
        <div class="filters">
          <mat-form-field appearance="outline" class="ifq-field search-filter">
            <mat-label>Search by Name or Customer Number</mat-label>
            <input
              matInput
              [(ngModel)]="searchText"
              (input)="onSearchChange()"
              (keyup.enter)="onSearchEnter()"
              placeholder="Enter at least 3 characters"
              type="text">
            <mat-hint *ngIf="searchText && searchText.trim().length > 0 && searchText.trim().length < 3">Minimum 3 characters required</mat-hint>
            <button
              mat-icon-button
              matSuffix
              *ngIf="searchText"
              (click)="clearSearch()"
              type="button"
              aria-label="Clear search">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>

          <mat-form-field
            appearance="outline"
            class="ifq-field producer-filter"
            *ngIf="isAdmin">
            <mat-label>Filter by Producer</mat-label>
            <mat-select
              [(value)]="selectedProducerId"
              (selectionChange)="onProducerFilterChange()"
              [disabled]="isLoadingProducers">
              <mat-option *ngFor="let producer of producerOptions" [value]="producer.id">
                {{ producer.displayName }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <button
            type="button"
            class="btn-ghost clear-filters"
            (click)="clearFilters()">
            <mat-icon aria-hidden="true">clear</mat-icon>
            Clear filters
          </button>
        </div>

        <!-- Quotes Table -->
        <div class="table-wrap">
          <table mat-table [dataSource]="quotes" matSort class="quotes-table">
            <!-- ID Column -->
            <ng-container matColumnDef="id">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>ID</th>
              <td mat-cell *matCellDef="let quote" class="mono">{{ quote.id }}</td>
            </ng-container>

            <!-- Customer Number Column -->
            <ng-container matColumnDef="customerNumber">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Customer Number</th>
              <td mat-cell *matCellDef="let quote" class="mono">{{ quote.customerNumber }}</td>
            </ng-container>

            <!-- Insured Name Column -->
            <ng-container matColumnDef="customerName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Insured Name</th>
              <td mat-cell *matCellDef="let quote" class="cell-name">{{ quote.insuredName }}</td>
            </ng-container>

            <!-- Producer (owning agent) Column -->
            <ng-container matColumnDef="producer">
              <th mat-header-cell *matHeaderCellDef>Producer</th>
              <td mat-cell *matCellDef="let quote">{{ quote.createdByProducerName || '—' }}</td>
            </ng-container>

            <!-- Status Column -->
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
              <td mat-cell *matCellDef="let quote">
                <span class="badge" [ngClass]="getStatusBadgeClass(quote.status)">
                  {{ getStatusDisplayName(quote.status) }}
                </span>
                <!-- KAN-43 · expired open offer -->
                <span class="badge badge-expired" *ngIf="isExpired(quote)" matTooltip="This offer expired — the customer link no longer works. Re-issue to send a fresh one.">
                  Expired
                </span>
              </td>
            </ng-container>

            <!-- Premium Column -->
            <ng-container matColumnDef="premium">
              <th mat-header-cell *matHeaderCellDef mat-sort-header class="cell-num">Total Premium</th>
              <td mat-cell *matCellDef="let quote" class="cell-num mono">{{ formatCurrency(getTotalPremium(quote)) }}</td>
            </ng-container>

            <!-- Created Date Column -->
            <ng-container matColumnDef="createdAt">
              <th mat-header-cell *matHeaderCellDef mat-sort-header class="cell-num">Created Date</th>
              <td mat-cell *matCellDef="let quote" class="cell-num mono">{{ formatDate(quote.createdAt) }}</td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="cell-actions">Actions</th>
              <td mat-cell *matCellDef="let quote" class="cell-actions actions-cell">
                <!--
                  KAN-40 · EDIT vs MANAGE gating.
                  A row is an ACCOUNT when Status != Quoted (an accepted, Stripe-
                  financed agreement — terms are locked). Accounts get "Manage"
                  (→ /account/:id servicing hub); the builder "Edit" is hidden
                  because editing an account is blocked server-side. QUOTE rows
                  (Status == Quoted) keep the builder "Edit".
                -->
                <button
                  mat-icon-button
                  class="action-account"
                  matTooltip="Manage account"
                  aria-label="Manage account"
                  (click)="$event.stopPropagation(); openAccount(quote)"
                  *ngIf="isAccount(quote)">
                  <mat-icon aria-hidden="true">manage_accounts</mat-icon>
                </button>
                <button
                  mat-icon-button
                  [matTooltip]="isAccount(quote) ? 'View original quote' : 'View Quote'"
                  [attr.aria-label]="isAccount(quote) ? 'View original quote' : 'View quote'"
                  (click)="$event.stopPropagation(); viewQuote(quote)"
                  *ngIf="canViewQuote(quote.status)">
                  <mat-icon aria-hidden="true">visibility</mat-icon>
                </button>
                <button mat-icon-button matTooltip="Send to Customer" aria-label="Send to customer" (click)="$event.stopPropagation(); emailQuote(quote)">
                  <mat-icon aria-hidden="true">send</mat-icon>
                </button>
                <!-- KAN-43 · re-issue an expired offer -->
                <button
                  mat-icon-button
                  class="action-reissue"
                  matTooltip="Re-issue (new customer link + 30 days)"
                  aria-label="Re-issue quote"
                  (click)="$event.stopPropagation(); reissue(quote)"
                  *ngIf="isExpired(quote)">
                  <mat-icon aria-hidden="true">autorenew</mat-icon>
                </button>
                <!-- Builder edit is offered for open quotes only (Status == Quoted). -->
                <button
                  mat-icon-button
                  matTooltip="Edit Quote"
                  aria-label="Edit quote"
                  (click)="$event.stopPropagation(); editQuote(quote)"
                  *ngIf="!isAccount(quote)">
                  <mat-icon aria-hidden="true">edit</mat-icon>
                </button>
                <button
                  mat-icon-button
                  class="action-delete"
                  matTooltip="Delete Quote"
                  aria-label="Delete quote"
                  (click)="$event.stopPropagation(); deleteQuote(quote)"
                  *ngIf="canDeleteQuote(quote)">
                  <mat-icon aria-hidden="true">delete</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" (click)="onRowClick(row)"></tr>
          </table>

          <!-- Loading Spinner -->
          <div *ngIf="isLoading" class="state-block">
            <mat-spinner diameter="40"></mat-spinner>
            <p>Loading quotes…</p>
          </div>

          <!-- No results Message -->
          <div *ngIf="!isLoading && quotes.length === 0" class="empty">
            <mat-icon class="empty-icon" aria-hidden="true">inbox</mat-icon>
            <h3>No {{ pageNoun }} found</h3>
            <p>No {{ pageNoun }} match your current filters. Try adjusting your search criteria or create a new quote.</p>
          </div>
        </div>

        <!-- Pagination -->
        <mat-paginator
          [length]="totalQuotes"
          [pageSize]="pageSize"
          [pageSizeOptions]="pageSizeOptions"
          [pageIndex]="currentPage"
          (page)="onPageChange($event)"
          showFirstLastButtons
          class="quotes-paginator">
        </mat-paginator>
      </div>
    </section>
  </div>
</div>
